import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AgentPerformanceTable = ({ timeRange, onAgentSelect, selectedAgent }) => {
  const [sortField, setSortField] = useState('volume');
  const [sortDirection, setSortDirection] = useState('desc');

  const agents = [
    {
      id: 'AGT-001',
      name: 'Michael Johnson',
      location: 'New York, NY',
      volume: 450000,
      transfers: 287,
      customers: 45,
      revenue: 9200,
      avgTicket: 1567,
      conversionRate: 94.2,
      approvalRate: 96.5,
      corridors: ['US→MX', 'US→IN', 'US→PH'],
      complianceScore: 96,
      suspensions: 0,
      geofenceAlerts: 2
    },
    {
      id: 'AGT-002',
      name: 'Sarah Williams',
      location: 'Los Angeles, CA',
      volume: 380000,
      transfers: 312,
      customers: 52,
      revenue: 7800,
      avgTicket: 1218,
      conversionRate: 91.5,
      approvalRate: 94.8,
      corridors: ['US→MX', 'US→GT', 'US→SV'],
      complianceScore: 94,
      suspensions: 1,
      geofenceAlerts: 0
    },
    {
      id: 'AGT-003',
      name: 'Robert Chen',
      location: 'San Francisco, CA',
      volume: 520000,
      transfers: 198,
      customers: 38,
      revenue: 10600,
      avgTicket: 2626,
      conversionRate: 96.8,
      approvalRate: 98.2,
      corridors: ['US→CN', 'US→IN', 'US→VN'],
      complianceScore: 98,
      suspensions: 0,
      geofenceAlerts: 0
    },
    {
      id: 'AGT-004',
      name: 'Maria Rodriguez',
      location: 'Houston, TX',
      volume: 425000,
      transfers: 356,
      customers: 67,
      revenue: 8700,
      avgTicket: 1194,
      conversionRate: 89.3,
      approvalRate: 92.1,
      corridors: ['US→MX', 'US→HN', 'US→NI'],
      complianceScore: 91,
      suspensions: 0,
      geofenceAlerts: 3
    },
    {
      id: 'AGT-005',
      name: 'David Kim',
      location: 'Chicago, IL',
      volume: 395000,
      transfers: 245,
      customers: 41,
      revenue: 8100,
      avgTicket: 1612,
      conversionRate: 93.7,
      approvalRate: 95.4,
      corridors: ['US→KR', 'US→PH', 'US→IN'],
      complianceScore: 95,
      suspensions: 0,
      geofenceAlerts: 1
    }
  ];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAgents = [...agents]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  })?.slice(0, 5);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getScoreColor = (score) => {
    if (score >= 95) return 'text-success';
    if (score >= 85) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Users" size={20} />
              <span>Top 5 Agents by Volume</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Performance ranking and key metrics
            </p>
          </div>
          <button className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 flex items-center space-x-2">
            <Icon name="Download" size={16} />
            <span>Export Report</span>
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Agent
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('volume')}
              >
                <div className="flex items-center space-x-1">
                  <span>Volume</span>
                  <Icon name={sortField === 'volume' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('revenue')}
              >
                <div className="flex items-center space-x-1">
                  <span>Revenue</span>
                  <Icon name={sortField === 'revenue' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('avgTicket')}
              >
                <div className="flex items-center space-x-1">
                  <span>Avg Ticket</span>
                  <Icon name={sortField === 'avgTicket' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('complianceScore')}
              >
                <div className="flex items-center space-x-1">
                  <span>Compliance</span>
                  <Icon name={sortField === 'complianceScore' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Alerts
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedAgents?.map((agent) => (
              <tr
                key={agent?.id}
                className={`hover:bg-muted/30 cursor-pointer ${
                  selectedAgent?.id === agent?.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                }`}
                onClick={() => onAgentSelect(agent)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {agent?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-foreground">{agent?.name}</div>
                      <div className="text-sm text-muted-foreground">{agent?.location}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">{formatCurrency(agent?.volume)}</div>
                  <div className="text-xs text-muted-foreground">{agent?.transfers} transfers</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">{formatCurrency(agent?.revenue)}</div>
                  <div className="text-xs text-muted-foreground">{agent?.customers} customers</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">{formatCurrency(agent?.avgTicket)}</div>
                  <div className="text-xs text-muted-foreground">{agent?.approvalRate}% approval</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-medium ${getScoreColor(agent?.complianceScore)}`}>
                    {agent?.complianceScore}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {agent?.conversionRate}% conversion
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    {agent?.suspensions > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error">
                        {agent?.suspensions} Susp.
                      </span>
                    )}
                    {agent?.geofenceAlerts > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                        {agent?.geofenceAlerts} Geo
                      </span>
                    )}
                    {agent?.suspensions === 0 && agent?.geofenceAlerts === 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        Clear
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-primary hover:text-primary/80">
                    <Icon name="Eye" size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentPerformanceTable;